
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { studentService } from '../../services/studentService';
import { programService } from '../../services/programService';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import AppIcon from '../../components/AppIcon';
import AdminLayout from '../../components/layouts/AdminLayout'; // Import Layout

const StudentForm = ({ embedded = false, studentId, onBack }) => {
    const navigate = useNavigate();

    const calculateStage = (moduleNum) => {
        const mod = Number(moduleNum) || 1;
        const year = Math.ceil(mod / 2);
        const sem = mod % 2 === 0 ? 2 : 1;
        return `Y${year}M${sem}`;
    };
    const { id: routeId } = useParams();
    const id = studentId || routeId;
    const isEditMode = !!id;

    const [formData, setFormData] = useState({
        first_name: '', middle_name: '', last_name: '',
        email: '', phone_number: '',
        gender: '', date_of_birth: '',
        nationality: 'Kenyan', id_number: '',
        program_id: '', academic_level: '', intake_code: '',
        admission_number: '', // Optional override
        starting_module: '1',
        status: 'Pending',
        notes: ''
    });

    // Intake selection state
    const [intakeMonth, setIntakeMonth] = useState('');
    const [intakeYear, setIntakeYear] = useState(new Date().getFullYear().toString());

    const [programs, setPrograms] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(isEditMode);
    const [error, setError] = useState('');

    useEffect(() => {
        loadPrograms();
        if (isEditMode) {
            loadStudent();
        }
    }, [id]);

    const loadPrograms = async () => {
        try {
            const data = await programService.getPrograms();
            setPrograms(data || []);
        } catch (err) {
            console.error(err);
        }
    };

    const loadStudent = async () => {
        try {
            const data = await studentService.getStudentById(id);
            if (data) {
                setFormData({
                    first_name: data.first_name || '',
                    middle_name: data.middle_name || '',
                    last_name: data.last_name || '',
                    email: data.email || '',
                    phone_number: data.phone_number || '',
                    gender: data.gender || '',
                    date_of_birth: data.date_of_birth || '',
                    nationality: data.nationality || '',
                    id_number: data.id_number || '',
                    program_id: data.program_id || '',
                    academic_level: data.academic_level || '',
                    intake_code: data.intake_code || '',
                    admission_number: data.admission_number || '',
                    starting_module: String(data.starting_module || '1'),
                    status: data.status || 'Pending',
                    notes: data.notes || ''
                });

                // Parse intake_code (e.g., "J26")
                if (data.intake_code && data.intake_code.length >= 3) {
                    const monthLetter = data.intake_code.charAt(0).toUpperCase();
                    const yearYY = data.intake_code.slice(1);

                    const monthMap = {
                        'J': 'January',
                        'M': 'May',
                        'S': 'September'
                    };

                    if (monthMap[monthLetter]) {
                        setIntakeMonth(monthMap[monthLetter]);
                    }

                    if (yearYY.length === 2) {
                        setIntakeYear(`20${yearYY}`);
                    }
                }
            }
        } catch (err) {
            setError("Failed to load student data.");
        } finally {
            setFetching(false);
        }
    };

    // Auto-generate Admission Number
    useEffect(() => {
        const triggerAutoGeneration = async () => {
            if (!isEditMode && formData.program_id && formData.academic_level && intakeMonth && intakeYear) {
                const intakeCodeLetter = intakeMonth.charAt(0).toUpperCase();
                const intakeYearYY = intakeYear.slice(-2);
                const formattedIntakeCode = `${intakeCodeLetter}${intakeYearYY}`;

                try {
                    const admissionNum = await studentService.generateAdmissionNumber(
                        formData.program_id,
                        formData.academic_level,
                        formattedIntakeCode
                    );
                    if (admissionNum) {
                        setFormData(prev => ({ ...prev, admission_number: admissionNum }));
                    }
                } catch (err) {
                    console.error("Auto-generation failed:", err);
                }
            }
        };

        triggerAutoGeneration();
    }, [formData.program_id, formData.academic_level, intakeMonth, intakeYear, isEditMode]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            // Basic validation
            if (!formData.first_name || !formData.last_name || !formData.email || !formData.program_id || !intakeMonth || !intakeYear) {
                throw new Error("Please fill in all required fields.");
            }

            // Combine month and year for intake_code (e.g., "J26")
            const intakeCodeLetter = intakeMonth.charAt(0).toUpperCase();
            const intakeYearYY = intakeYear.slice(-2);
            const formattedIntakeCode = `${intakeCodeLetter}${intakeYearYY}`;

            // Update formData with the combined intake value
            const submissionData = {
                ...formData,
                intake_code: formattedIntakeCode
            };

            if (isEditMode) {
                const res = await studentService.updateStudent(id, submissionData);
                if (!res.success) throw new Error(res.error);
            } else {
                const payload = { ...submissionData };
                const res = await studentService.createStudent(payload);
                if (!res.success) throw new Error(res.error);
            }

            onBack ? onBack() : navigate('/students');
        } catch (err) {
            setError(err.message || "An error occurred.");
        } finally {
            setLoading(false);
        }
    };

    if (fetching) return <div className="p-10 text-center">Loading...</div>;

    const Layout = embedded ? React.Fragment : ({ children }) => <AdminLayout title={isEditMode ? 'Edit Student' : 'New Admission'}>{children}</AdminLayout>;
    return (
        <Layout>
            <div className="max-w-4xl mx-auto p-6 space-y-6">
                <div className="flex items-center space-x-4 mb-6">
                    <Button variant="ghost" onClick={() => onBack ? onBack() : navigate('/students')}>
                        <AppIcon name="arrow-left" className="mr-2 h-4 w-4" />
                        Back
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold">{isEditMode ? 'Edit Student' : 'New Admission'}</h1>
                        <p className="text-muted-foreground">Enter student details and admission information.</p>
                    </div>
                </div>

                {error && (
                    <div className="bg-red-50 text-red-600 p-4 rounded-md mb-4 border border-red-200">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <Card className="mb-6">
                        <CardHeader>
                            <CardTitle>Personal Information</CardTitle>
                        </CardHeader>
                        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <label className="block text-sm font-medium mb-1">First Name *</label>
                                <Input name="first_name" value={formData.first_name} onChange={handleChange} required />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Middle Name</label>
                                <Input name="middle_name" value={formData.middle_name} onChange={handleChange} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Last Name *</label>
                                <Input name="last_name" value={formData.last_name} onChange={handleChange} required />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Email *</label>
                                <Input name="email" type="email" value={formData.email} onChange={handleChange} required />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Phone Number</label>
                                <Input name="phone_number" value={formData.phone_number} onChange={handleChange} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Gender</label>
                                <Select
                                    value={formData.gender}
                                    onChange={(value) => handleSelectChange('gender', value)}
                                    options={[
                                        { value: '', label: 'Select' },
                                        { value: 'Male', label: 'Male' },
                                        { value: 'Female', label: 'Female' },
                                        { value: 'Other', label: 'Other' }
                                    ]}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Date of Birth</label>
                                <Input name="date_of_birth" type="date" value={formData.date_of_birth} onChange={handleChange} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Nationality</label>
                                <Input name="nationality" value={formData.nationality} onChange={handleChange} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">ID/Passport No (Unique)</label>
                                <Input name="id_number" value={formData.id_number} onChange={handleChange} />
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="mb-6">
                        <CardHeader>
                            <CardTitle>Admission Details</CardTitle>
                        </CardHeader>
                        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium mb-1">Program *</label>
                                <Select
                                    value={formData.program_id}
                                    onChange={(value) => handleSelectChange('program_id', value)}
                                    options={[
                                        { value: '', label: 'Select Program' },
                                        ...programs.map(p => ({ value: p.id, label: `${p.code} - ${p.name}` }))
                                    ]}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Level *</label>
                                <Select
                                    value={formData.academic_level}
                                    onChange={(value) => handleSelectChange('academic_level', value)}
                                    disabled={!formData.program_id}
                                    options={[
                                        { value: '', label: 'Select Level' },
                                        ...(
                                            (() => {
                                                const selectedProgram = programs.find(p => p.id === formData.program_id);
                                                if (selectedProgram && selectedProgram.levels && selectedProgram.levels.length > 0) {
                                                    const dbLevelMapping = {
                                                        'level_3': 'Level 3 (NVC)',
                                                        'level_4_artisan': 'Level 4 (Artisan)',
                                                        'level_5_craft': 'Level 5 (Craft)',
                                                        'level_6_diploma': 'Level 6 (Diploma)'
                                                    };
                                                    return selectedProgram.levels.map(lvl => ({
                                                        value: dbLevelMapping[lvl] || lvl, // Use mapped label as value for readability in student record, or DB value? User likely wants readable.
                                                        label: dbLevelMapping[lvl] || lvl
                                                    }));
                                                }
                                                return []; // If no levels defined, show nothing (or could show generic list if needed, but user asked for strict filtering)
                                            })()
                                        )
                                    ]}
                                />
                                {!formData.program_id && (
                                    <p className="text-xs text-muted-foreground mt-1">Please select a program first.</p>
                                )}
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Intake Month *</label>
                                    <Select
                                        value={intakeMonth}
                                        onChange={setIntakeMonth}
                                        options={[
                                            { value: '', label: 'Month' },
                                            { value: 'January', label: 'January' },
                                            { value: 'May', label: 'May' },
                                            { value: 'September', label: 'September' }
                                        ]}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Intake Year *</label>
                                    <Select
                                        value={intakeYear}
                                        onChange={setIntakeYear}
                                        options={[
                                            ...Array.from({ length: 5 }, (_, i) => {
                                                const year = new Date().getFullYear() + i;
                                                return { value: String(year), label: String(year) };
                                            })
                                        ]}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Starting Module</label>
                                <div className="flex items-center space-x-3">
                                    <Input
                                        name="starting_module"
                                        type="number"
                                        min="1"
                                        value={formData.starting_module}
                                        onChange={handleChange}
                                        className="w-24"
                                    />
                                    <div className="bg-blue-50 px-3 py-2 rounded border border-blue-100 flex flex-col">
                                        <span className="text-[10px] uppercase font-bold text-blue-600 leading-none">Stage</span>
                                        <span className="text-sm font-bold text-blue-800">{calculateStage(formData.starting_module)}</span>
                                    </div>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">Defaults to 1. Change for lateral entry.</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Admission Number</label>
                                <Input
                                    name="admission_number"
                                    value={formData.admission_number}
                                    onChange={handleChange}
                                    placeholder="Auto-generated if blank"
                                    disabled={!isEditMode && false} // Allow manual override if needed
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Status</label>
                                <Select
                                    value={formData.status}
                                    onChange={(value) => handleSelectChange('status', value)}
                                    options={[
                                        { value: 'Pending', label: 'Pending' },
                                        { value: 'Admitted', label: 'Admitted' },
                                        { value: 'Active', label: 'Active' },
                                        { value: 'Inactive', label: 'Inactive' },
                                        { value: 'Suspended', label: 'Suspended' },
                                        { value: 'Withdrawn', label: 'Withdrawn' }
                                    ]}
                                />
                            </div>
                        </CardContent>
                    </Card>

                    <div className="flex justify-end space-x-4">
                        <Button type="button" variant="outline" onClick={() => onBack ? onBack() : navigate('/students')}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={loading}>
                            {loading ? 'Saving...' : isEditMode ? 'Update Student' : 'Create Admission'}
                        </Button>
                    </div>
                </form>
            </div>
        </Layout>
    );
};

export default StudentForm;
